import React, { useCallback, useEffect, useState } from 'react';
import { Container, CircularProgress, Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { NotificationSettingsApi } from '../api/NotificationSettingsApi';
import { setSuccessNotification, setErrorNotification } from '../../../actions/notificationAction';
import useTranslation from '../../../hooks/useTranslation';
import { NotificationSettings } from '../../../shared-components';
import { useParams } from 'react-router-dom';

const INITIAL_FORM = { notify_low_stock: true, notify_tracking: true, notify_waitlist: true };

const NotificationSettingsContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { store_id } = useParams();

  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const notifyError = useCallback(
    (e) => {
      dispatch(setErrorNotification({ message: e?.response?.data?.message || 'Something went wrong' }));
    },
    [dispatch]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await NotificationSettingsApi.getNotificationSettings(store_id);
      setForm({
        notify_low_stock: !!data?.notify_low_stock,
        notify_tracking: !!data?.notify_tracking,
        notify_waitlist: !!data?.notify_waitlist,
      });
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  }, [notifyError, store_id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = useCallback((key, checked) => {
    setForm((prev) => ({ ...prev, [key]: checked }));
  }, []);

  const onSave = useCallback(async () => {
    try {
      setSaving(true);
      await NotificationSettingsApi.updateNotificationSettings(store_id, form);
      dispatch(setSuccessNotification({ message: t('response_messages.updated') }));
    } catch (e) {
      notifyError(e);
    } finally {
      setSaving(false);
    }
  }, [dispatch, form, notifyError, t, store_id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <NotificationSettings
        value={form}
        onChange={handleChange}
        onSave={onSave}
        saving={saving}
        description={t('labels.notification_settings_desc')}
      />
    </Container>
  );
};

export default NotificationSettingsContainer;